import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { LogOut, Users, Calendar, MessageSquare, Shield, UserCog, AlertCircle, UserPlus, UserCheck, Edit, Trash2, Power, Plus, BarChart, Video, LayoutDashboard, Menu, Sparkles, ClipboardCheck, Target, ShieldCheck, FileText, Mail } from "lucide-react";
import { hashPassword } from "@/lib/security";
import { handleRateLimitError } from "@/lib/rateLimitHandler";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import RecordingManager from "@/components/RecordingManager";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useActivityFeed } from "@/hooks/useActivityFeed";
import { BlogManager } from "@/components/admin/BlogManager";
import { FeedbackManager } from "@/components/admin/FeedbackManager";
import { ContactManager } from "@/components/admin/ContactManager";

interface User {
  id: string;
  email: string;
  display_name: string;
  created_at: string;
  role?: 'superadmin' | 'admin' | 'moderator' | 'user';
}

const ROLE_DESCRIPTIONS = {
  superadmin: {
    label: 'Super Admin',
    color: 'destructive' as const,
    description: 'Full system access with ability to manage all users, roles, and settings',
    permissions: ['Manage all users', 'Assign any role', 'Delete meetings', 'View all data', 'System configuration']
  },
  admin: {
    label: 'Admin',
    color: 'default' as const,
    description: 'Can manage users, meetings, and moderate content',
    permissions: ['Manage users', 'Moderate meetings', 'View analytics', 'Delete inappropriate content']
  },
  moderator: {
    label: 'Moderator',
    color: 'secondary' as const,
    description: 'Can moderate meetings and manage basic user issues',
    permissions: ['Moderate meetings', 'Mute/remove participants', 'View meeting reports', 'Basic user support']
  },
  user: {
    label: 'User',
    color: 'outline' as const,
    description: 'Standard user with ability to create and join meetings',
    permissions: ['Create meetings', 'Join meetings', 'Send messages', 'Manage own profile']
  }
};

interface Meeting {
  id: string;
  title: string;
  description: string | null;
  meeting_code: string;
  host_id: string;
  is_active: boolean;
  created_at: string;
  host_name?: string;
}

interface ChatMessage {
  id: string;
  message: string;
  created_at: string;
  user_name: string;
  meeting_title?: string;
}

interface FeatureProgram {
  id: string;
  name: string;
  description: string;
  owner: string;
  stage: "alpha" | "beta" | "general";
  enabled: boolean;
  metric: string;
  category: "collaboration" | "governance" | "reliability";
  nextStep: string;
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { role, isSuperAdmin, isAdmin, loading: roleLoading } = useUserRole();
  const { toast } = useToast();
  const { addActivity } = useActivityFeed();
  
  const [users, setUsers] = useState<User[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserDisplayName, setNewUserDisplayName] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState<'superadmin' | 'admin' | 'moderator' | 'user'>('user');
  const [assignUserOpen, setAssignUserOpen] = useState(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [editMeetingOpen, setEditMeetingOpen] = useState(false);
  const [deleteMeetingOpen, setDeleteMeetingOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [meetingTitle, setMeetingTitle] = useState("");
  const [meetingDescription, setMeetingDescription] = useState("");
  const [meetingPassword, setMeetingPassword] = useState("");
  const [createMeetingOpen, setCreateMeetingOpen] = useState(false);
  const [newMeetingHostId, setNewMeetingHostId] = useState<string>("");
  const [featurePrograms, setFeaturePrograms] = useState<FeatureProgram[]>([
    {
      id: "collab-cursors",
      name: "Collaboration cursors",
      description: "Color-coded cursors, selections, and presence indicators for multi-editor flows.",
      owner: "Product",
      stage: "beta",
      enabled: true,
      metric: "68% of active workspaces have multi-editor sessions",
      category: "collaboration",
      nextStep: "Ship cursor avatars to the canvas toolbar"
    },
    {
      id: "ai-suggestions",
      name: "Contextual AI suggestions",
      description: "Inline smart prompts that propose next steps, validations, and missing safeguards.",
      owner: "AI/ML",
      stage: "alpha",
      enabled: true,
      metric: "42% of drafts accept at least one AI quick action",
      category: "collaboration",
      nextStep: "Add confidence badges and explainability snippets"
    },
    {
      id: "guided-validation",
      name: "Guided validation & evidence",
      description: "Checklists that require screenshots, files, or URLs before steps can be completed.",
      owner: "Compliance",
      stage: "beta",
      enabled: true,
      metric: "31% reduction in incomplete runs",
      category: "governance",
      nextStep: "Roll out auto-detect for missing SLA owners"
    },
    {
      id: "version-approvals",
      name: "Version snapshots & approvals",
      description: "Snapshot flows, generate human-readable diffs, and gate launches with multi-approver reviews.",
      owner: "Governance",
      stage: "beta",
      enabled: false,
      metric: "12 teams piloting approvals",
      category: "governance",
      nextStep: "Enable Slack notifications for approvals queue"
    },
    {
      id: "execution-playback",
      name: "Execution playback & incident mode",
      description: "Time-based replay with incident freeze, timestamps, and command palette navigation.",
      owner: "Reliability",
      stage: "alpha",
      enabled: false,
      metric: "Playback enabled on 6 critical runbooks",
      category: "reliability",
      nextStep: "Expose filters for user, status, and tags during replay"
    },
    {
      id: "offline-sync",
      name: "Offline sync & smart routing",
      description: "Queue edits locally, sync when online, and route notifications by role and time windows.",
      owner: "Platform",
      stage: "alpha",
      enabled: true,
      metric: "Queue flush latency under 400ms",
      category: "reliability",
      nextStep: "Add digest mode for noisy flows"
    },
    {
      id: "marketplace-blocks",
      name: "Marketplace of blocks",
      description: "Reusable subflows and integrations with ratings, changelog visibility, and auto-updates.",
      owner: "Ecosystem",
      stage: "alpha",
      enabled: false,
      metric: "18 reusable blocks drafted",
      category: "collaboration",
      nextStep: "Launch curated starters for onboarding and incident response"
    },
    {
      id: "data-retention",
      name: "Data retention & redaction",
      description: 'Per-flow retention, PII redaction, and one-click "redact now" actions for completed runs.',
      owner: "Security",
      stage: "general",
      enabled: true,
      metric: "Default retention policies applied to 74% of flows",
      category: "governance",
      nextStep: "Expose export-to-PDF with traceability metadata"
    }
  ]);

  const toggleFeatureProgram = (featureId: string) => {
    setFeaturePrograms(prev => prev.map(feature =>
      feature.id === featureId ? { ...feature, enabled: !feature.enabled } : feature
    ));
  };

  const setFeatureStage = (featureId: string, stage: FeatureProgram["stage"]) => {
    setFeaturePrograms(prev => prev.map(feature =>
      feature.id === featureId ? { ...feature, stage } : feature
    ));
  };

  const fetchDirectoryUser = async (userId: string) => {
    const { data, error } = await supabase.functions.invoke("admin-tools", {
      body: {
        action: "get_user",
        payload: { userId }
      }
    });

    if (error || data?.error || !data?.user) {
      console.error("Fallback directory lookup failed", error || data?.error);
      return null;
    }

    return {
      email: data.user.email as string | null,
      display_name: (data.user as any).display_name as string | null,
      role: (data.user as any).role as string | null
    };
  };

  useEffect(() => {
    if (roleLoading || role === null) return;

    if (!isAdmin) {
      toast({
        title: "Access Denied",
        description: "You need admin privileges to view this page.",
        variant: "destructive",
      });
      navigate("/");
      return;
    }

    if (isSuperAdmin) {
      fetchAdminData();
      return;
    }

    // Admins can access the analytics module without loading superadmin data
    setActiveTab("analytics");
    setLoading(false);
  }, [role, isSuperAdmin, isAdmin, roleLoading, navigate, toast]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      // Pull authoritative emails/roles from the admin tools Edge Function
      const { data: adminDirectory, error: directoryError } = await supabase.functions.invoke('admin-tools', {
        body: { action: 'list_users' }
      });

      if (directoryError) {
        console.error('Admin directory lookup failed', directoryError);
      }

      const directoryMap = new Map(
        (adminDirectory?.users || []).map((user: any) => [
          user.id,
          {
            email: user.email,
            role: user.role,
            display_name: user.display_name
          }
        ])
      );

      // Fetch users with their profiles and roles
      const { data: usersData, error: usersError } = await supabase
        .from("profiles")
        .select(`
          id,
          user_id,
          display_name,
          created_at
        `)
        .order("created_at", { ascending: false });

      if (usersError) throw usersError;

      // Fetch user roles separately
      const { data: rolesData, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role");

      if (rolesError) throw rolesError;

      // Create role map
      const roleMap = new Map();
      rolesData?.forEach(roleRecord => {
        roleMap.set(roleRecord.user_id, roleRecord.role);
      });

      const transformedUsers = await Promise.all((usersData || []).map(async (user) => {
        let directoryUser = directoryMap.get(user.user_id) as { email: string | null; role: string | null; display_name: string | null } | undefined;

        if (!directoryUser?.email || !directoryUser?.display_name) {
          const fallbackUser = await fetchDirectoryUser(user.user_id);
          if (fallbackUser) {
            directoryUser = {
              email: directoryUser?.email || fallbackUser.email,
              role: directoryUser?.role || fallbackUser.role,
              display_name: directoryUser?.display_name || fallbackUser.display_name
            };
            directoryMap.set(user.user_id, directoryUser);
          }
        }

        const roleFromDirectory = directoryUser?.role ? directoryUser.role : null;
        return {
          id: user.user_id,
          email: directoryUser?.email || "(email unavailable)",
          display_name: directoryUser?.display_name || user.display_name,
          created_at: user.created_at,
          role: (roleFromDirectory || roleMap.get(user.user_id) || 'user') as 'superadmin' | 'admin' | 'moderator' | 'user'
        };
      }));

      setUsers(transformedUsers);

      // Fetch meetings with host information
      const { data: meetingsData, error: meetingsError } = await supabase
        .from("meetings")
        .select(`
          id,
          title,
          description,
          meeting_code,
          host_id,
          is_active,
          created_at
        `)
        .order("created_at", { ascending: false })
        .limit(50);

      if (meetingsError) throw meetingsError;

      // Fetch host profiles separately
      const hostIds = meetingsData?.map(m => m.host_id) || [];
      const { data: hostProfiles, error: hostError } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", hostIds);

      if (hostError) throw hostError;

      const hostMap = new Map();
      hostProfiles?.forEach(profile => {
        hostMap.set(profile.user_id, profile.display_name);
      });

      const transformedMeetings = meetingsData?.map(meeting => ({
        ...meeting,
        host_name: hostMap.get(meeting.host_id) || 'Unknown'
      })) || [];

      setMeetings(transformedMeetings);

      // Fetch recent chat messages
      const { data: chatData, error: chatError } = await supabase
        .from("chat_messages")
        .select(`
          id,
          message,
          created_at,
          user_id,
          meeting_id
        `)
        .order("created_at", { ascending: false })
        .limit(100);

      if (chatError) throw chatError;

      // Get user and meeting info for chat messages
      const userIds = [...new Set(chatData?.map(c => c.user_id) || [])];
      const meetingIds = [...new Set(chatData?.map(c => c.meeting_id) || [])];

      const { data: chatUsers, error: chatUsersError } = await supabase
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", userIds);

      const { data: chatMeetings, error: chatMeetingsError } = await supabase
        .from("meetings")
        .select("id, title")
        .in("id", meetingIds);

      if (chatUsersError || chatMeetingsError) throw chatUsersError || chatMeetingsError;

      const userMap = new Map();
      const meetingMap = new Map();
      
      chatUsers?.forEach(user => userMap.set(user.user_id, user.display_name));
      chatMeetings?.forEach(meeting => meetingMap.set(meeting.id, meeting.title));

      const transformedChat = chatData?.map(chat => ({
        ...chat,
        user_name: userMap.get(chat.user_id) || 'Unknown',
        meeting_title: meetingMap.get(chat.meeting_id) || 'Unknown Meeting'
      })) || [];

      setChatMessages(transformedChat);

    } catch (error: any) {
      console.error('Error fetching admin data:', error);
      toast({
        title: "Error",
        description: "Failed to load admin data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'superadmin' | 'admin' | 'moderator' | 'user') => {
    try {
      const targetUser = users.find((u) => u.id === userId);

      // Delete existing role
      const { error: deleteError } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId);

      if (deleteError) throw deleteError;

      // Insert new role
      const { error: insertError } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role: newRole });

      if (insertError) throw insertError;

      toast({
        title: "Role Updated",
        description: `User role changed to ${ROLE_DESCRIPTIONS[newRole].label}`,
      });

      if (user) {
        addActivity({
          action: "assigned",
          actor: { id: user.id, name: user.email },
          target: { title: `${ROLE_DESCRIPTIONS[newRole].label} to ${targetUser?.display_name || "user"}` },
        });
      }

      // Refresh data
      fetchAdminData();
    } catch (error: any) {
      console.error('Error updating role:', error);
      toast({
        title: "Error",
        description: "Failed to update user role.",
        variant: "destructive",
      });
    }
  };

  const handleCreateUser = async () => {
    if (!newUserEmail.trim() || !newUserDisplayName.trim() || !newUserPassword.trim()) {
      toast({
        title: "Validation Error",
        description: "Email, display name, and password are required.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("admin-tools", {
        body: {
          action: "create_user",
          payload: {
            email: newUserEmail.trim(),
            password: newUserPassword.trim(),
            displayName: newUserDisplayName.trim(),
            role: newUserRole,
          },
        },
      });

      if (error || data?.error) {
        throw error || new Error(data?.error || "Failed to create user");
      }

      toast({
        title: "User Created",
        description: `${newUserDisplayName} has been added with the ${newUserRole} role.`,
      });

      setCreateUserOpen(false);
      setNewUserEmail("");
      setNewUserDisplayName("");
      setNewUserPassword("");
      setNewUserRole('user');
      fetchAdminData();
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast({
        title: "Error",
        description: error?.message || "Failed to create user.",
        variant: "destructive",
      });
    }
  };

  const generateMeetingCode = () => {
    const array = new Uint8Array(9);
    crypto.getRandomValues(array);
    
    // Convert to base36 (0-9, a-z) and uppercase
    return Array.from(array)
      .map(byte => (byte % 36).toString(36))
      .join('')
      .toUpperCase()
      .substring(0, 9);
  };

  const handleCreateMeeting = async () => {
    const selectedHost = newMeetingHostId || user?.id;

    if (!meetingTitle.trim() || !selectedHost) {
      toast({
        title: "Validation Error",
        description: "Meeting title and host are required.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("admin-tools", {
        body: {
          action: "create_meeting",
          payload: {
            title: meetingTitle.trim(),
            description: meetingDescription.trim() || null,
            hostId: selectedHost,
            password: meetingPassword.trim() || null,
          },
        },
      });

      if (error || data?.error) {
        throw error || new Error(data?.error || "Failed to create meeting");
      }

      toast({
        title: "Meeting Created",
        description: `Meeting created successfully with code: ${data?.meeting?.meeting_code}`,
      });

      if (user) {
        addActivity({
          action: "created",
          actor: { id: user.id, name: user.email },
          target: { title: `meeting "${meetingTitle}"` },
        });
      }

      setCreateMeetingOpen(false);
      setMeetingTitle("");
      setMeetingDescription("");
      setMeetingPassword("");
      setNewMeetingHostId("");
      fetchAdminData();
    } catch (error: any) {
      console.error('Error creating meeting:', error);
      
      // Check if this is a rate limit error
      const isRateLimit = handleRateLimitError(error, 'meeting_create');
      
      if (!isRateLimit) {
        toast({
          title: "Error",
          description: "Failed to create meeting.",
          variant: "destructive",
        });
      }
    }
  };

  const handleEditMeeting = async () => {
    if (!selectedMeeting || !meetingTitle.trim()) {
      toast({
        title: "Validation Error",
        description: "Meeting title is required.",
        variant: "destructive",
      });
      return;
    }

    try {
      const updateData: any = {
        title: meetingTitle,
        description: meetingDescription || null,
      };

      if (meetingPassword.trim()) {
        // Hash the new password before storing
        updateData.password = await hashPassword(meetingPassword);
      }

      const { error } = await supabase
        .from("meetings")
        .update(updateData)
        .eq("id", selectedMeeting.id);

      if (error) throw error;

      toast({
        title: "Meeting Updated",
        description: "Meeting has been successfully updated.",
      });

      if (user) {
        addActivity({
          action: "edited",
          actor: { id: user.id, name: user.email },
          target: { title: `meeting "${meetingTitle}"` },
        });
      }

      setEditMeetingOpen(false);
      setSelectedMeeting(null);
      setMeetingTitle("");
      setMeetingDescription("");
      setMeetingPassword("");
      fetchAdminData();
    } catch (error: any) {
      console.error('Error updating meeting:', error);
      toast({
        title: "Error",
        description: "Failed to update meeting.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteMeeting = async () => {
    if (!selectedMeeting) return;

    try {
      const { error } = await supabase
        .from("meetings")
        .delete()
        .eq("id", selectedMeeting.id);

      if (error) throw error;

      toast({
        title: "Meeting Deleted",
        description: "Meeting has been successfully deleted.",
      });

      setDeleteMeetingOpen(false);
      setSelectedMeeting(null);
      fetchAdminData();
    } catch (error: any) {
      console.error('Error deleting meeting:', error);
      toast({
        title: "Error",
        description: "Failed to delete meeting.",
        variant: "destructive",
      });
    }
  };

  const handleToggleMeetingStatus = async (meetingId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from("meetings")
        .update({ is_active: !currentStatus })
        .eq("id", meetingId);

      if (error) throw error;

      toast({
        title: "Status Updated",
        description: `Meeting ${!currentStatus ? 'activated' : 'ended'} successfully.`,
      });

      fetchAdminData();
    } catch (error: any) {
      console.error('Error updating meeting status:', error);
      toast({
        title: "Error",
        description: "Failed to update meeting status.",
        variant: "destructive",
      });
    }
  };

  const handleAssignUserToMeeting = async () => {
    if (!selectedMeetingId || !selectedUserId) {
      toast({
        title: "Validation Error",
        description: "Please select both a meeting and a user.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Check if user is already a participant
      const { data: existingParticipant } = await supabase
        .from("meeting_participants")
        .select("id")
        .eq("meeting_id", selectedMeetingId)
        .eq("user_id", selectedUserId)
        .maybeSingle();

      if (existingParticipant) {
        toast({
          title: "Already Assigned",
          description: "This user is already a participant in this meeting.",
        });
        return;
      }

      // Add user as participant
      const { error } = await supabase
        .from("meeting_participants")
        .insert({
          meeting_id: selectedMeetingId,
          user_id: selectedUserId,
          role: 'participant'
        });

      if (error) throw error;

      toast({
        title: "User Assigned",
        description: "User has been successfully assigned to the meeting.",
      });

      setAssignUserOpen(false);
      setSelectedMeetingId(null);
      setSelectedUserId("");
      fetchAdminData();
    } catch (error: any) {
      console.error('Error assigning user:', error);
      toast({
        title: "Error",
        description: "Failed to assign user to meeting.",
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  if (roleLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  const enabledFeatureCount = featurePrograms.filter(feature => feature.enabled).length;
  const betaFeatureCount = featurePrograms.filter(feature => feature.stage === "beta").length;
  const generalFeatureCount = featurePrograms.filter(feature => feature.stage === "general").length;

  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "analytics", label: "Analytics", icon: BarChart },
    { id: "recordings", label: "Recordings", icon: Video },
    { id: "blog", label: "Blog Posts", icon: FileText },
    { id: "feedback", label: "Feedback", icon: MessageSquare },
    { id: "contact", label: "Contact Us", icon: Mail },
    { id: "users", label: "User Management", icon: Users },
    { id: "roles", label: "Roles & Permissions", icon: Shield },
    { id: "meetings", label: "Meetings", icon: Calendar },
    { id: "chat", label: "Chat Activity", icon: MessageSquare },
    { id: "innovations", label: "Feature Programs", icon: Sparkles },
  ];

  const visibleMenuItems = isSuperAdmin
    ? menuItems
    : menuItems.filter(item => item.id === "analytics" || item.id === "blog");

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <div className="fixed bottom-4 left-4 z-40 md:hidden">
          <SidebarTrigger className="h-12 w-12 rounded-full shadow-lg" aria-label="Open admin navigation" />
        </div>
        <Sidebar className="border-r">
          <SidebarContent>
            <div className="p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Shield className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg">Admin Panel</h2>
                  <p className="text-xs text-muted-foreground">Control Center</p>
                </div>
              </div>
            </div>
            
            <SidebarGroup>
              <SidebarGroupLabel>Navigation</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleMenuItems.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        onClick={() => setActiveTab(item.id)}
                        isActive={activeTab === item.id}
                        className="w-full"
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            
            <div className="mt-auto p-4 border-t">
              <div className="space-y-2">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start" 
                  onClick={() => navigate("/")}
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  Dashboard
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
                      <AlertDialogDescription>
                        You will need to sign in again to access the admin panel.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleSignOut}>
                        Logout
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </SidebarContent>
        </Sidebar>

        <div className="flex-1 flex flex-col">
          <header className="h-16 border-b flex items-center px-6 bg-card">
            <SidebarTrigger className="mr-4" />
            <div className="flex items-center justify-between w-full">
              <div>
                <h1 className="text-xl font-semibold">
                  {menuItems.find(item => item.id === activeTab)?.label}
                </h1>
                <p className="text-sm text-muted-foreground">
                  Welcome back, {user?.email}
                </p>
              </div>
            </div>
          </header>

          <main className="flex-1 p-6 overflow-auto">
            {activeTab === "dashboard" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                      <Users className="h-5 w-5 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-primary">{users.length}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Registered accounts
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-secondary/10 to-secondary/5 border-secondary/20">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Meetings</CardTitle>
                      <Calendar className="h-5 w-5 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-primary">{meetings.length}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        All time meetings
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Active Meetings</CardTitle>
                      <Video className="h-5 w-5 text-accent" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-accent">
                        {meetings.filter(m => m.is_active).length}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Currently live
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Chat Messages</CardTitle>
                      <MessageSquare className="h-5 w-5 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-primary">{chatMessages.length}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Total messages
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Activity</CardTitle>
                      <CardDescription>Latest meetings and user activity</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {meetings.slice(0, 5).map((meeting) => (
                          <div key={meeting.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                            <div className="flex items-center space-x-3">
                              <div className={`h-2 w-2 rounded-full ${meeting.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                              <div>
                                <p className="font-medium text-sm">{meeting.title}</p>
                                <p className="text-xs text-muted-foreground">{meeting.host_name}</p>
                              </div>
                            </div>
                            <Badge variant={meeting.is_active ? "default" : "secondary"}>
                              {meeting.is_active ? "Live" : "Ended"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>User Distribution</CardTitle>
                      <CardDescription>Users by role</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(ROLE_DESCRIPTIONS).map(([roleKey, roleInfo]) => {
                          const count = users.filter(u => u.role === roleKey).length;
                          const percentage = users.length > 0 ? (count / users.length) * 100 : 0;
                          return (
                            <div key={roleKey} className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="font-medium">{roleInfo.label}</span>
                                <span className="text-muted-foreground">{count} users</span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary rounded-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
            
            {activeTab === "analytics" && (
              <AnalyticsDashboard />
            )}
            
            {activeTab === "recordings" && (
              <RecordingManager showMeetingTitle={true} />
            )}

            {activeTab === "blog" && (
              <BlogManager />
            )}

            {activeTab === "feedback" && (
              <FeedbackManager />
            )}

            {activeTab === "contact" && (
              <ContactManager />
            )}
            
            {activeTab === "users" && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <UserCog className="h-5 w-5" />
                      <CardTitle>User Management</CardTitle>
                    </div>
                    <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
                      <DialogTrigger asChild>
                        <Button>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Create User
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Create New User</DialogTitle>
                          <DialogDescription>
                            Add a new user to the system with a specific role.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                              id="email"
                              type="email"
                              placeholder="user@example.com"
                              value={newUserEmail}
                              onChange={(e) => setNewUserEmail(e.target.value)}
                            />
                          </div>
                        <div className="space-y-2">
                          <Label htmlFor="displayName">Display Name</Label>
                          <Input
                            id="displayName"
                            placeholder="John Doe"
                            value={newUserDisplayName}
                            onChange={(e) => setNewUserDisplayName(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="password">Temporary Password</Label>
                          <PasswordInput
                            id="password"
                            placeholder="Set an initial password"
                            value={newUserPassword}
                            onChange={(e) => setNewUserPassword(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="role">Role</Label>
                          <Select value={newUserRole} onValueChange={(value: any) => setNewUserRole(value)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="moderator">Moderator</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="superadmin">Super Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setCreateUserOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleCreateUser}>Create User</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <CardDescription>
                    Manage user accounts and assign roles
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Display Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((userItem) => (
                        <TableRow key={userItem.id}>
                          <TableCell className="font-medium">
                            {userItem.display_name}
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-[200px]">
                            <span className="block truncate" title={userItem.email}>
                              {userItem.email}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={ROLE_DESCRIPTIONS[userItem.role || 'user'].color}>
                              {ROLE_DESCRIPTIONS[userItem.role || 'user'].label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {new Date(userItem.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={userItem.role || 'user'}
                              onValueChange={(value) => handleRoleChange(userItem.id, value as any)}
                              disabled={userItem.id === user?.id}
                            >
                              <SelectTrigger className="w-[150px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="superadmin">Super Admin</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="moderator">Moderator</SelectItem>
                                <SelectItem value="user">User</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
            
            {activeTab === "roles" && (
              <div className="grid gap-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Role Management</AlertTitle>
                  <AlertDescription>
                    Understanding roles and their permissions helps maintain proper access control across your platform.
                  </AlertDescription>
                </Alert>

                {Object.entries(ROLE_DESCRIPTIONS).map(([roleKey, roleInfo]) => (
                  <Card key={roleKey}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Shield className="h-5 w-5 text-primary" />
                          <CardTitle>{roleInfo.label}</CardTitle>
                        </div>
                        <Badge variant={roleInfo.color}>
                          {users.filter(u => u.role === roleKey).length} users
                        </Badge>
                      </div>
                      <CardDescription>{roleInfo.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold">Permissions:</p>
                        <ul className="space-y-1">
                          {roleInfo.permissions.map((permission, idx) => (
                            <li key={idx} className="text-sm text-muted-foreground flex items-center">
                              <span className="mr-2">•</span>
                              {permission}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            {activeTab === "meetings" && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Meeting Management</CardTitle>
                      <CardDescription>
                        Create, edit, and manage all meetings
                      </CardDescription>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Dialog open={createMeetingOpen} onOpenChange={setCreateMeetingOpen}>
                        <DialogTrigger asChild>
                          <Button>
                            <Plus className="h-4 w-4 mr-2" />
                            Create Meeting
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Create New Meeting</DialogTitle>
                            <DialogDescription>
                              Create a meeting for any user as the host
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="create-title">Title</Label>
                              <Input
                                id="create-title"
                                placeholder="Meeting title"
                                value={meetingTitle}
                                onChange={(e) => setMeetingTitle(e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="create-description">Description (Optional)</Label>
                              <Input
                                id="create-description"
                                placeholder="Meeting description"
                                value={meetingDescription}
                                onChange={(e) => setMeetingDescription(e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="create-host">Host</Label>
                              <Select value={newMeetingHostId} onValueChange={setNewMeetingHostId}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select meeting host" />
                                </SelectTrigger>
                                <SelectContent>
                                  {users.map((user) => (
                                    <SelectItem key={user.id} value={user.id}>
                                      {user.display_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="create-password">Password (Optional)</Label>
                              <PasswordInput
                                id="create-password"
                                placeholder="Leave empty for no password"
                                value={meetingPassword}
                                onChange={(e) => setMeetingPassword(e.target.value)}
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setCreateMeetingOpen(false)}>
                              Cancel
                            </Button>
                            <Button onClick={handleCreateMeeting}>Create Meeting</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                      
                      <Dialog open={assignUserOpen} onOpenChange={setAssignUserOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline">
                            <UserCheck className="h-4 w-4 mr-2" />
                            Assign User
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Assign User to Meeting</DialogTitle>
                            <DialogDescription>
                              Add a user as a participant to a specific meeting.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="meeting">Select Meeting</Label>
                              <Select value={selectedMeetingId || ""} onValueChange={setSelectedMeetingId}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Choose a meeting" />
                                </SelectTrigger>
                                <SelectContent>
                                  {meetings.filter(m => m.is_active).map((meeting) => (
                                    <SelectItem key={meeting.id} value={meeting.id}>
                                      {meeting.title} ({meeting.meeting_code})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="user">Select User</Label>
                              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Choose a user" />
                                </SelectTrigger>
                                <SelectContent>
                                  {users.map((user) => (
                                    <SelectItem key={user.id} value={user.id}>
                                      {user.display_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setAssignUserOpen(false)}>
                              Cancel
                            </Button>
                            <Button onClick={handleAssignUserToMeeting}>Assign User</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Host</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {meetings.map((meeting) => (
                        <TableRow key={meeting.id}>
                          <TableCell className="font-medium">
                            {meeting.title}
                          </TableCell>
                          <TableCell className="font-mono">
                            {meeting.meeting_code}
                          </TableCell>
                          <TableCell>{meeting.host_name}</TableCell>
                          <TableCell>
                            <Badge variant={meeting.is_active ? 'default' : 'secondary'}>
                              {meeting.is_active ? 'Active' : 'Ended'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {new Date(meeting.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleMeetingStatus(meeting.id, meeting.is_active)}
                                title={meeting.is_active ? 'End Meeting' : 'Activate Meeting'}
                              >
                                <Power className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedMeeting(meeting);
                                  setMeetingTitle(meeting.title);
                                  setMeetingDescription(meeting.description || "");
                                  setEditMeetingOpen(true);
                                }}
                                title="Edit Meeting"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedMeeting(meeting);
                                  setDeleteMeetingOpen(true);
                                }}
                                title="Delete Meeting"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
                
                <Dialog open={editMeetingOpen} onOpenChange={setEditMeetingOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Edit Meeting</DialogTitle>
                      <DialogDescription>
                        Update meeting details. Leave password empty to keep current password.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-title">Title</Label>
                        <Input
                          id="edit-title"
                          placeholder="Meeting title"
                          value={meetingTitle}
                          onChange={(e) => setMeetingTitle(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-description">Description (Optional)</Label>
                        <Input
                          id="edit-description"
                          placeholder="Meeting description"
                          value={meetingDescription}
                          onChange={(e) => setMeetingDescription(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-password">New Password (Optional)</Label>
                        <PasswordInput
                          id="edit-password"
                          placeholder="Leave empty to keep current"
                          value={meetingPassword}
                          onChange={(e) => setMeetingPassword(e.target.value)}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setEditMeetingOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleEditMeeting}>Save Changes</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={deleteMeetingOpen} onOpenChange={setDeleteMeetingOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Delete Meeting</DialogTitle>
                      <DialogDescription>
                        Are you sure you want to delete "{selectedMeeting?.title}"? This action cannot be undone.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDeleteMeetingOpen(false)}>
                        Cancel
                      </Button>
                      <Button variant="destructive" onClick={handleDeleteMeeting}>
                        Delete Meeting
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </Card>
            )}

            {activeTab === "innovations" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Enabled programs</CardTitle>
                      <Sparkles className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{enabledFeatureCount}</div>
                      <p className="text-xs text-muted-foreground">Live feature rollouts</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">In beta</CardTitle>
                      <ClipboardCheck className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{betaFeatureCount}</div>
                      <p className="text-xs text-muted-foreground">Require targeted QA</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Generally available</CardTitle>
                      <ShieldCheck className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{generalFeatureCount}</div>
                      <p className="text-xs text-muted-foreground">Ready for all tenants</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Governance coverage</CardTitle>
                      <Target className="h-4 w-4 text-primary" />
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const governanceTotal = featurePrograms.filter(feature => feature.category === "governance").length;
                        const governanceEnabled = featurePrograms.filter(feature => feature.category === "governance" && feature.enabled).length;
                        const coverage = governanceTotal ? Math.round((governanceEnabled / governanceTotal) * 100) : 0;
                        return (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-semibold">{coverage}%</span>
                              <span className="text-muted-foreground">{governanceEnabled}/{governanceTotal} programs</span>
                            </div>
                            <Progress value={coverage} />
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader className="flex items-start justify-between">
                    <div>
                      <CardTitle>Feature rollout control</CardTitle>
                      <CardDescription>Enable, stage, and monitor the differentiating capabilities requested for the product.</CardDescription>
                    </div>
                    <Badge variant="secondary">Feature lab</Badge>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {featurePrograms.map(feature => (
                      <div key={feature.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{feature.name}</h3>
                              <Badge variant={feature.enabled ? "default" : "outline"}>{feature.stage.toUpperCase()}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{feature.description}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Switch
                              checked={feature.enabled}
                              onCheckedChange={() => toggleFeatureProgram(feature.id)}
                              aria-label={`Toggle ${feature.name}`}
                            />
                            <Select value={feature.stage} onValueChange={(value: FeatureProgram["stage"]) => setFeatureStage(feature.id, value)}>
                              <SelectTrigger className="w-[140px]">
                                <SelectValue placeholder="Stage" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="alpha">Alpha</SelectItem>
                                <SelectItem value="beta">Beta</SelectItem>
                                <SelectItem value="general">General</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid gap-3 md:grid-cols-3 text-sm">
                          <div>
                            <p className="text-muted-foreground">Owner</p>
                            <p className="font-medium">{feature.owner}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Success metric</p>
                            <p className="font-medium">{feature.metric}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Next step</p>
                            <p className="font-medium">{feature.nextStep}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Operational guardrails</CardTitle>
                    <CardDescription>Keep the new capabilities safe, auditable, and ready for rollout.</CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <h4 className="font-semibold">Governance & approvals</h4>
                      <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                        <li>Require approvers before moving version snapshots to general availability.</li>
                        <li>Auto-attach evidence when validation checklists block a step.</li>
                        <li>Escalate pending approvals via notifications with time windows.</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold">Resilience & privacy</h4>
                      <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                        <li>Keep incident mode ready to freeze definitions during live issues.</li>
                        <li>Apply retention defaults and enable on-demand redaction for sensitive runs.</li>
                        <li>Queue offline edits and retry with jitter to avoid data loss.</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === "chat" && (
              <Card>
                <CardHeader>
                  <CardTitle>Chat Activity</CardTitle>
                  <CardDescription>
                    Recent chat messages across all meetings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {chatMessages.map((message) => (
                      <div key={message.id} className="border-l-2 border-primary/20 pl-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{message.user_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(message.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm mb-1">{message.message}</p>
                        <span className="text-xs text-muted-foreground">
                          in {message.meeting_title}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}